import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeMediaId = '';

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);

    const firstMedia = this.refs.media?.[0];
    if (firstMedia) this.setActiveMedia(this.mediaIdFor(firstMedia), { dispatch: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number|{media_id?: string}} target - The media index or actual Shopify media id.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(target, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const index = this.indexForTarget(target);
    if (index < 0) return;
    const targetImage = media[index];
    const mediaId = this.mediaIdFor(targetImage);
    const targetThumbnail = this.thumbnailForMediaId(mediaId) || thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      this.setActiveMedia(mediaId, { dispatch: false });

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectMedia(mediaId, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /** @param {number|{media_id?: string}} target */
  indexForTarget(target) {
    if (typeof target === 'object' && target?.media_id) {
      const mediaId = String(target.media_id);
      return this.refs.media.findIndex((item) => this.mediaIdFor(item) === mediaId);
    }
    if (typeof target === 'string' && target.trim() && !/^\d+$/.test(target.trim())) {
      return this.refs.media.findIndex((item) => this.mediaIdFor(item) === target.trim());
    }
    const index = Number(target);
    return Number.isInteger(index) ? index : -1;
  }

  /** @param {number|{media_id?: string}} target */
  mediaForTarget(target) {
    const index = this.indexForTarget(target);
    return index >= 0 ? this.refs.media[index] : undefined;
  }

  /** @param {HTMLElement} media */
  mediaIdFor(media) {
    return media?.dataset.mediaId || media?.querySelector('[data-media-id]')?.dataset.mediaId || '';
  }

  /** @param {string} mediaId */
  thumbnailForMediaId(mediaId) {
    if (!mediaId || !this.refs.thumbnails) return undefined;
    return Array.from(this.refs.thumbnails.children).find((thumbnail) => thumbnail.dataset.mediaId === String(mediaId));
  }

  /**
   * Keep the dialog's media, thumbnail, accessibility, and playback state in
   * sync from one Shopify media id.
   * @param {string} mediaId
   * @param {{dispatch?: boolean}} options
   */
  setActiveMedia(mediaId, { dispatch = true } = {}) {
    const normalizedId = String(mediaId || '');
    if (!normalizedId) return -1;

    const { media, thumbnails } = this.refs;
    const index = media.findIndex((item) => this.mediaIdFor(item) === normalizedId);
    if (index < 0) return -1;

    const targetMedia = media[index];
    this.#activeMediaId = normalizedId;

    media.forEach((item) => {
      const active = item === targetMedia;
      item.setAttribute('aria-hidden', String(!active));
      item.toggleAttribute('data-lightbox-active', active);
      if (!active) {
        item.querySelectorAll('video').forEach((video) => video.pause());
      }
    });

    Array.from(thumbnails.children).forEach((thumbnail) => {
      thumbnail.setAttribute('aria-selected', String(thumbnail.dataset.mediaId === normalizedId));
    });

    if (dispatch) this.dispatchMediaSelected(index);
    return index;
  }

  /** @param {number} index */
  dispatchMediaSelected(index) {
    const event = new ZoomMediaSelectedEvent(index);
    event.detail.mediaId = this.mediaIdFor(this.refs.media[index]);
    this.dispatchEvent(event);
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const mediaId = this.mediaIdFor(mostVisibleElement);
    const activeIndex = this.setActiveMedia(mediaId);
    const targetThumbnail = this.thumbnailForMediaId(mediaId) || thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.loadHighResolutionImage(mostVisibleElement);
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const mediaId = this.mediaIdFor(mostVisibleElement);
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.find((item) => item.dataset.mediaId === mediaId || item.querySelector(`[data-media-id="${mediaId}"]`))
      : mediaGallery?.media?.find((item) => item.dataset.mediaId === mediaId);

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(target) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const index = this.indexForTarget(target);
    if (index < 0) return;
    this.selectMedia(this.mediaIdFor(this.refs.media[index]), { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(target) {
    const media = this.mediaForTarget(target);
    if (!media) return;

    this.loadHighResolutionImage(media);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.media?.length || !Number.isInteger(index)) return;
    const targetMedia = this.refs.media[index];
    if (!targetMedia) return;
    this.selectMedia(this.mediaIdFor(targetMedia), options);
  }

  /**
   * Select a lightbox item by its Shopify media id.
   * @param {string} mediaId
   * @param {{behavior?: ScrollBehavior}} options
   */
  selectMedia(mediaId, options = { behavior: 'smooth' }) {
    const index = this.setActiveMedia(mediaId);
    if (index < 0) return;

    const targetThumbnail = this.thumbnailForMediaId(mediaId);
    if (targetThumbnail) {
      scrollIntoView(targetThumbnail, {
        ancestor: this.refs.thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = this.refs.media[index];
    targetImage?.scrollIntoView({ behavior: options.behavior, block: 'nearest', inline: 'nearest' });
    if (targetImage) this.loadHighResolutionImage(targetImage);
  }

  /** @param {Event} event */
  next(event) {
    event?.preventDefault();
    this.selectRelativeMedia(1);
  }

  /** @param {Event} event */
  previous(event) {
    event?.preventDefault();
    this.selectRelativeMedia(-1);
  }

  /** @param {number} offset */
  selectRelativeMedia(offset) {
    const { media } = this.refs;
    if (!media?.length) return;
    const currentIndex = media.findIndex((item) => this.mediaIdFor(item) === this.#activeMediaId);
    const baseIndex = currentIndex >= 0 ? currentIndex : 0;
    const targetIndex = (baseIndex + offset + media.length) % media.length;
    this.selectMedia(this.mediaIdFor(media[targetIndex]), {
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
